import React, { useState, useEffect } from 'react';
import { AppHeader } from './AppHeader';
import { Sidebar } from './Sidebar';
import { NotificationContainer } from '../ui/NotificationContainer';
import { AccessibilityButton } from '../ui/AccessibilitySettings';
import { SkipToContent } from '../ui/SkipToContent';
import { useAccessibility } from '../../contexts/AccessibilityContext';

interface MainLayoutProps {
  children: React.ReactNode;
}

export const MainLayout: React.FC<MainLayoutProps> = ({ children }) => {
  const [isMobileMenuOpen, setIsMobileMenuOpen] = useState(false);
  const { announce } = useAccessibility();

  const handleMenuToggle = () => {
    const newState = !isMobileMenuOpen;
    setIsMobileMenuOpen(newState);
    announce(`Navigation menu ${newState ? 'opened' : 'closed'}`, false);
  };

  const handleMenuClose = () => {
    setIsMobileMenuOpen(false);
    announce('Navigation menu closed', false);
  };

  // Handle escape key to close mobile menu
  useEffect(() => {
    const handleEscape = (e: KeyboardEvent) => {
      if (e.key === 'Escape' && isMobileMenuOpen) {
        handleMenuClose();
      }
    };

    document.addEventListener('keydown', handleEscape);
    return () => document.removeEventListener('keydown', handleEscape);
  }, [isMobileMenuOpen]);

  return (
    <div className="min-h-screen bg-gray-50">
      {/* Skip to content link */}
      <SkipToContent contentId="main-content" />
      
      {/* Mobile menu overlay */}
      {isMobileMenuOpen && (
        <div 
          className="fixed inset-0 z-40 bg-black bg-opacity-50 lg:hidden"
          onClick={handleMenuClose}
          aria-hidden="true"
        />
      )}

      {/* Sidebar */}
      <Sidebar isOpen={isMobileMenuOpen} onClose={handleMenuClose} />

      {/* Header - fixed at top */}
      <AppHeader 
        onMenuToggle={handleMenuToggle} 
        isMobileMenuOpen={isMobileMenuOpen} 
      />

      {/* Main content area */}
      <div className="lg:pl-64 pt-16">
        <main 
          id="main-content" 
          className="flex-1" 
          tabIndex={-1}
          role="main"
          aria-label="Main content"
        >
          <div className="mx-auto max-w-7xl px-4 py-6 sm:px-6 lg:px-8">
            {children}
          </div>
        </main>
      </div>

      {/* Notification container */}
      <NotificationContainer />
      
      {/* Accessibility button */}
      <AccessibilityButton />
      
      {/* Screen reader announcements for layout changes */}
      <div className="sr-only" aria-live="polite" aria-atomic="true">
        {isMobileMenuOpen ? 'Navigation menu is open' : ''}
      </div>
    </div>
  );
};